import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { useAssignedProjects, type AssignmentField } from '@/data/useAssignedProjects'
import type { ProjectRecord } from '@/data/useProjects'
import { AssignedProjectList } from './AssignedProjectList'

interface AssignedProjectsPageProps {
  title: string
  field: AssignmentField
}

function byStartDateDesc(a: ProjectRecord, b: ProjectRecord): number {
  return (b.startDate?.toMillis?.() ?? 0) - (a.startDate?.toMillis?.() ?? 0)
}

/** Shared supplier/client dashboard: assigned projects split into Ongoing / Completed. */
export function AssignedProjectsPage({ title, field }: AssignedProjectsPageProps) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { projects, loading, error } = useAssignedProjects(field, user?.uid ?? '')

  const { ongoing, completed } = useMemo(() => {
    const sorted = [...projects].sort(byStartDateDesc)
    return {
      ongoing: sorted.filter((project) => project.status === 'ongoing'),
      completed: sorted.filter((project) => project.status === 'completed'),
    }
  }, [projects])

  function open(project: ProjectRecord) {
    navigate(`projects/${project.id}`)
  }

  return (
    <div>
      <h1 className="mb-6 text-2xl font-semibold text-slate-800">{title}</h1>
      {loading && <p className="text-slate-500">Loading projects…</p>}
      {error && (
        <p role="alert" className="text-danger">
          {error}
        </p>
      )}
      {!loading && !error && (
        <div className="space-y-8">
          <AssignedProjectList title="Ongoing" projects={ongoing} onOpen={open} />
          <AssignedProjectList
            title="Completed"
            projects={completed}
            showCompletion
            onOpen={open}
          />
        </div>
      )}
    </div>
  )
}
